// Small components of the business-first kit. Each one renders glossary labels, never raw strings, so the
// vocabulary on business screens is governed in one place.
import React from 'react';
import { Link } from 'react-router-dom';
import { meaning, t, type Basis, type Explain, type TileData } from '@leo/model';
import { ExplainButton, plainExplain } from './explain';
import { reportMeta } from './report';

const asOfWords = (s: string) => { const d = new Date(s); return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); };

/** "Based on: bills to 10 Oct · stock as of 19 Oct" — the provenance of every figure, one tap away, never in the headline. */
export function BasisChips({ basis }: { basis: Basis[] }) {
  React.useEffect(() => { if (basis?.length) reportMeta.set({ basis }); }, [basis]);   // exports and printouts name their source
  if (!basis?.length) return null;
  return <span className="basis" title={meaning('basis')}>{t('basis')}: {basis.map((b, i) => <span key={i} className="bchip">{b.source} {b.asOf ? `to ${asOfWords(b.asOf)}` : ''}</span>)}</span>;
}

/** One sentence on where this area stands, then its basis. */
export function StatusLine({ text, basis, tone }: { text: string; basis: Basis[]; tone?: 'good' | 'needs' | 'under' | 'neutral' }) {
  return <div className={`status ${tone ?? 'neutral'}`}><p>{text}</p><BasisChips basis={basis} /></div>;
}

export function Tile({ label, value, sub, to, tone, explain }: TileData & { explain?: Explain }) {
  const card = (
    <Link to={to} className={`tile ${tone ?? 'neutral'} has-x`} title={meaning(label)}>
      <span className="k">{t(label)}</span>
      <span className="v num">{value}</span>
      {sub && <span className="s">{sub}</span>}
    </Link>
  );
  // every tile opens the one Explain panel (its own explanation, else its meaning from the glossary), with "See the rows"
  return <div className="kpiw">{card}<ExplainButton explain={explain ?? plainExplain(t(label), value, meaning(label) || undefined)} className="kpi-x" label={t(label)} rowsTo={to} /></div>;
}

export function Tiles({ tiles }: { tiles: TileData[] }) { return <div className="tiles">{tiles.map(x => <Tile key={x.to + x.label} {...x} />)}</div>; }

/** An area that switches on when the named exports arrive. Says what it will show, what unlocks it, and what is already known. */
export function WaitingCard({ areaKey, unlocks, alreadyKnown, note, compact }: { areaKey: string; unlocks: string[]; alreadyKnown?: { label: string; value: string }[]; note?: string; compact?: boolean }) {
  return (
    <section className="waiting">
      <div className="xs">{t('waiting.title')}</div>
      {!compact && <h2>{t(areaKey)}</h2>}
      {!compact && <p className="lead">{meaning(areaKey)}</p>}
      {note && <p className={compact ? 'lead' : 'muted'}>{note}</p>}
      <div className="wgrid">
        <div>
          <div className="xs">{t('waiting.unlocks')}</div>
          <ul>{unlocks.map(u => <li key={u}>{u}</li>)}</ul>
        </div>
        {alreadyKnown && alreadyKnown.length > 0 && (
          <div>
            <div className="xs">{t('waiting.already')}</div>
            <dl className="kv">{alreadyKnown.map(k => <React.Fragment key={k.label}><dt title={meaning(k.label)}>{t(k.label)}</dt><dd className="num">{k.value}</dd></React.Fragment>)}</dl>
          </div>
        )}
      </div>
    </section>
  );
}

/** A band on an area page: title from the glossary, optional right-hand action, content. */
export function Band({ title, action, children, id }: { title: string; action?: React.ReactNode; children: React.ReactNode; id?: string }) {
  return <section className="band" id={id}><header><h2 title={meaning(title)}>{t(title)}</h2>{action}</header>{children}</section>;
}

export function Breadcrumbs({ items }: { items: { label: string; onClick?: () => void }[] }) {
  return <nav className="crumbs">{items.map((c, i) => <span key={i}>{c.onClick ? <button className="link" onClick={c.onClick}>{c.label}</button> : <b>{c.label}</b>}{i < items.length - 1 && <span className="sep">›</span>}</span>)}</nav>;
}

export const Skeleton = ({ lines = 3 }: { lines?: number }) => <div className="skel" aria-busy="true">{Array.from({ length: lines }, (_, i) => <i key={i} />)}</div>;
